import { URLS } from "config/urls";
import { ChatOutlinedIcon, TodayIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { reverse } from "utils/urls";

import Card, { CardActionArea } from "components/Card";
import Link from "components/Link";
import Stack from "components/Stack";
import Text from "components/Text";
import Tooltip from "components/Tooltip";

import { IChecklistAccessibleToNonPayingCustomer } from "entities/Deal/sdk";
import LightningIcon from "entities/Field/components/FieldUpdateIcon/assets/lightningIcon.svg";

export interface IChecklistBox {
  checklist: IChecklistAccessibleToNonPayingCustomer;
}

const ChecklistBox = ({ checklist }: IChecklistBox) => (
  <Link
    to={reverse(URLS.DEAL_PACKAGE_NO_TAB, {
      dealId: checklist.deal.id,
      packageId: checklist.id,
    })}
  >
    <Card
      sx={{
        flexDirection: "column",
        borderRadius: 3,
        height: "fit-content",
        boxShadow: `0 4px 8px 0 ${colors.gray20}`,
        transition: "boxShadow 0 linear",
        "&:hover": {
          boxShadow: `0 4px 8px 0 ${colors.gray60}`,
        },
      }}
      data-testid="checklist-card"
    >
      <CardActionArea
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          padding: theme.spacing(1, 2),
          gap: 1,
        }}
        data-testid="checklist-card-content"
      >
        <Stack>
          <Text
            fontSize={16}
            fontWeight={800}
            variant="text2"
            data-testid="checklist-name"
          >
            {checklist.name}
          </Text>
          <Text
            fontSize={15}
            fontWeight={400}
            variant="text2"
            data-testid="checklist-owner-company-name"
          >
            {checklist.company.name}
          </Text>
        </Stack>
        <Stack
          sx={{
            backgroundColor: colors.blue20,
            borderRadius: "4px",
            width: "fit-content",
          }}
          padding={theme.spacing(0.4, 0.8)}
        >
          <Text
            fontWeight={700}
            fontSize={13}
            variant="text2"
            data-testid="checklist-deal-name"
          >
            {checklist.deal.name}
          </Text>
        </Stack>
        <Stack direction="row" gap={0.5} flexWrap="wrap">
          <Tooltip title="Updated items">
            <Stack
              padding={theme.spacing(0.5, 1)}
              direction="row"
              alignItems="center"
              gap={0.75}
              sx={{
                borderRadius: "16px",
                backgroundColor: colors.gray5,
                border: `1px solid ${colors.gray60}`,
              }}
            >
              <Stack
                sx={{
                  backgroundColor: colors.blue100,
                }}
                borderRadius="10px"
                alignItems="center"
                justifyContent="center"
                height="16px"
                width="16px"
              >
                <img
                  src={LightningIcon}
                  alt=""
                  width="6px"
                  height="8px"
                  data-testid="field-updated-icon"
                />
              </Stack>

              <Text variant="text4" fontWeight={500} color={colors.gray100}>
                {checklist.updated_field_count}
              </Text>
            </Stack>
          </Tooltip>

          <Tooltip title="Open tasks">
            <Stack
              padding={theme.spacing(0.5, 1)}
              direction="row"
              alignItems="center"
              gap={0.75}
              sx={{
                borderRadius: "16px",
                backgroundColor: colors.gray5,
                border: `1px solid ${colors.gray60}`,
              }}
            >
              <TodayIcon
                sx={{ height: "16px", width: "16px", color: colors.blue100 }}
              />

              <Text variant="text4" fontWeight={500} color={colors.gray100}>
                {checklist.open_tasks_count}
              </Text>
            </Stack>
          </Tooltip>

          <Tooltip title="Unread messages">
            <Stack
              padding={theme.spacing(0.5, 1)}
              direction="row"
              alignItems="center"
              gap={0.75}
              sx={{
                borderRadius: "16px",
                backgroundColor: colors.gray5,
                border: `1px solid ${colors.gray60}`,
              }}
            >
              <ChatOutlinedIcon
                sx={{ height: "16px", width: "16px", color: colors.blue100 }}
              />

              <Text variant="text4" fontWeight={500} color={colors.gray100}>
                {checklist.unread_messages_count}
              </Text>
            </Stack>
          </Tooltip>
        </Stack>
      </CardActionArea>
    </Card>
  </Link>
);

export default ChecklistBox;
